
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Phone, Loader2, Link as LinkIcon, UserCheck, CheckCircle2, AlertTriangle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { formatPhoneNumber } from "@/utils/phoneValidation";

export function AdminPhoneSettings() {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [linkedProfile, setLinkedProfile] = useState<{id: string, phone_number: string} | null>(null);
  const [linkedStatus, setLinkedStatus] = useState<'linked' | 'unlinked' | 'loading'>('loading');
  const [error, setError] = useState<string | null>(null);

  // Fetch current phone number and linked status on component mount
  useEffect(() => {
    const fetchAdminPhone = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError || !session) {
          throw new Error(sessionError?.message || "Not authenticated");
        }

        // Use the RPC function instead of querying the admin_users table directly
        // This avoids the infinite recursion in the policy
        const { data: isAdmin, error: adminCheckError } = await supabase.rpc('is_admin');
        if (adminCheckError || !isAdmin) {
          console.error("Admin check failed:", adminCheckError);
          throw new Error("Not authorized as admin");
        }

        // Use alternative query approach to avoid infinite recursion
        const { data: adminUserView, error: viewError } = await supabase
          .from('admin_user_view')  // Use the view instead of direct table access
          .select('phone_number')
          .eq('auth_user_id', session.user.id)
          .maybeSingle();

        if (viewError) {
          console.error("Error fetching admin data:", viewError);
          throw viewError;
        }

        if (adminUserView?.phone_number) {
          setPhoneNumber(adminUserView.phone_number);
          
          // Check if this phone number is linked to a profile
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('id, phone_number')
            .eq('phone_number', adminUserView.phone_number)
            .eq('admin_user_id', session.user.id)
            .maybeSingle();
            
          if (!profileError && profileData) {
            setLinkedProfile(profileData);
            setLinkedStatus('linked');
          } else {
            setLinkedStatus('unlinked');
          }
        } else {
          setLinkedStatus('unlinked');
        }
      } catch (error: any) {
        console.error('Error fetching phone number:', error);
        toast.error("Failed to fetch phone number");
      } finally {
        setIsFetching(false);
      }
    };

    fetchAdminPhone();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        throw new Error(sessionError?.message || "Not authenticated");
      }

      // Refresh the session to ensure we have a valid token
      const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError || !refreshData.session) {
        throw new Error("Failed to refresh session: " + (refreshError?.message || "Unknown error"));
      }

      const formattedPhone = formatPhoneNumber(phoneNumber);
      const response = await fetch("https://dpuqliwrenamnnwsnbhb.supabase.co/functions/v1/link-admin-profile", {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${refreshData.session.access_token}`
        },
        body: JSON.stringify({ phoneNumber: formattedPhone })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to link phone number");
      }

      if (result.profile) {
        setLinkedProfile(result.profile);
        setLinkedStatus('linked');
      }
      
      toast.success(result.message || "Phone number linked successfully");
      
    } catch (error: any) {
      console.error('Error updating phone number:', error);
      setError(error.message || "Failed to update phone number");
      toast.error(error.message || "Failed to update phone number");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Link Phone Number</CardTitle>
        <CardDescription>
          Connect your admin account to your user profile by setting the same phone number
        </CardDescription>
      </CardHeader>
      <CardContent>
        {linkedStatus === 'linked' && linkedProfile && (
          <Alert className="mb-4 bg-green-50 border-green-100">
            <CheckCircle2 className="h-4 w-4 text-green-500" />
            <AlertDescription className="flex items-center gap-2">
              <UserCheck className="h-4 w-4 text-green-500" />
              <span>Successfully linked to user profile with phone number {linkedProfile.phone_number}</span>
            </AlertDescription>
          </Alert>
        )}
        
        {error && (
          <Alert className="mb-4 bg-red-50 border-red-100">
            <AlertTriangle className="h-4 w-4 text-red-500" />
            <AlertDescription className="text-red-500">
              {error}
            </AlertDescription>
          </Alert>
        )}
        
        {isFetching ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <div className="flex gap-2">
                <Input
                  id="phone"
                  type="tel"
                  placeholder="Enter your phone number"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  pattern="^(\+?61|0)[4-5]\d{8}$"
                  required
                />
                <Button 
                  type="submit" 
                  disabled={loading || linkedStatus === 'linked'}
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : linkedStatus === 'linked' ? (
                    <CheckCircle2 className="h-4 w-4" />
                  ) : (
                    <LinkIcon className="h-4 w-4" />
                  )}
                  <span className="ml-2">
                    {linkedStatus === 'linked' ? 'Linked' : 'Link'}
                  </span>
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">
                Format: 0412345678 or +61412345678
              </p>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
}
